<script lang="ts">
    import Fa from 'svelte-fa'
    import { faEye, faEdit, faPrint } from '@fortawesome/free-solid-svg-icons'
    import Content from '../../_common/content.svelte'
    import LoadSpinner from '../../_common/load-spinner.svelte'
    import { onMount } from 'svelte'
    import dayjs from 'dayjs'
    import DateSelect from '../../_common/date-select.svelte'
    import Popover from '../../_common/popover.svelte'
    import { GridButton, GridClass, GridCol, GridColTypeEnum, GridLine } from '../../_common/z/zgrid'
    import Zgrid from '../../_common/z/zgrid.svelte'
    import Financial_4RelatResultDetail from './financial-4-relat-result-detail.svelte'
    import Zchart from '../../_common/z/zchart.svelte'
    import { ChartData, type ChartBarsSeries } from '../../_common/z/zchart'
    import Zframe from '../../_common/z/zframe.svelte'
    import type { Account } from '../../../modules/financialApi/accounts/account'
    import type { Entry } from '../../../modules/financialApi/entries/entry'
    import type { Category } from '../../../modules/financialApi/categories/category'
    import { EntryTypeEnum } from '../../../modules/financialApi/entries/entry.enum'
    import Zflow from '../../_common/z/zflow.svelte'
    import Zact from '../../_common/z/zact.svelte'
    import DateSelectMonth from '../../_common/date-select-month.svelte'
    import Zinput from '../../_common/z/zinput.svelte'

    let account: Account = null

    let show = false

    let dates: { from: Date; to: Date } = null
    dates = window.api.utils.dates.currentMonth()
    let dateSelect

    function dateChanged(event) {
        dates = event.detail
        loadEntities()
    }

    onMount(async () => {
        await loadEntities()
    })

    interface Relat {
        category: Category
        total: number
        entries: Entry[]
        show: boolean
        debitCredit: EntryTypeEnum
        previousTotal: number
        previousEntries: Entry[]
    }

    let relat: Relat[] = []

    let showPrevious = false
    async function loadEntities() {
        try {
            show = true
            let entries: Entry[] = []
            const filter = {
                from: dates?.from || undefined,
                to: dates?.to || undefined,
                accountId: account?.accountId || undefined,
            }
            // limita a navegação por mais de 7 dias
            if (!window.store.token.permissions.financial.entries.navigate) {
                const diff = dayjs(new Date()).diff(filter.from, 'days')
                if (diff > 7) {
                    dates.from = dayjs(new Date()).add(-7, 'day').toDate()
                    filter.from = dates.from
                    dateSelect.setDate(dates.from, dates.to)
                }
            }

            entries = await window.api.financials.entries.getAll(filter)
            entries = await window.api.utils.utils.assignPersonsFromObject(entries)
            console.log('financial.entries', entries)
            let previousEntries: Entry[] = []
            if (showPrevious) {
                const previousFilter = {
                    from: dayjs(dates.from).add(-1, 'month').toDate(),
                    to: dayjs(dates.to).add(-1, 'month').toDate(),
                    accountId: account?.accountId || undefined,
                }
                previousEntries = await window.api.financials.entries.getAll(previousFilter)
                previousEntries = await window.api.utils.utils.assignPersonsFromObject(previousEntries)
            }

            relat = []
            let categories: Category[] = await window.api.financials.categories.getAll({ removeds: true })
            console.log('financial.categories', categories)
            for (const cat of categories) {
                const filtered = entries.filter((el) => el.categoryId === cat.categoryId)
                const filteredPrevious = previousEntries.filter((el) => el.categoryId === cat.categoryId)
                if (filtered.length > 0 || filteredPrevious.length > 0) {
                    let item: Relat = {
                        category: cat,
                        entries: filtered,
                        total: filtered.reduce((acc, el) => (acc += el.value), 0),
                        show: false,
                        debitCredit: filtered[0]?.type || filteredPrevious[0]?.type,
                        previousTotal: filteredPrevious.reduce((acc, el) => (acc += el.value), 0),
                        previousEntries: filteredPrevious,
                    }
                    relat.push(item)
                }
            }

            relat = relat

            loadGridCredit()
            loadGridDebit()
            loadGridCreditNoImpact()
            loadGridDebitNoImpact()

            loadChartCredit()
            loadChartDebit()
            loadChartFlow(entries)
        } catch (error) {
            window.error(error)
        } finally {
            show = false
        }
    }

    let gridCredit: GridClass = new GridClass()
    function loadGridCredit() {
        // grid
        gridCredit = new GridClass()
        // grid head
        gridCredit.title = 'Receitas'
        gridCredit.subtitle = ''
        gridCredit.fontSize = '15px'
        gridCredit.showActs = false
        gridCredit.cols.push(new GridCol({ content: 'Categoria', type: GridColTypeEnum.string }))
        if (showPrevious) {
            gridCredit.cols.push(new GridCol({ content: 'Período anterior', type: GridColTypeEnum.float_2, showTotal: true }))
            gridCredit.cols.push(new GridCol({ content: '', type: GridColTypeEnum.button }))
        }
        gridCredit.cols.push(new GridCol({ content: 'Valor', type: GridColTypeEnum.float_2, showTotal: true }))
        gridCredit.cols.push(new GridCol({ content: '', type: GridColTypeEnum.button }))
        if (showPrevious) {
            gridCredit.cols.push(new GridCol({ content: 'Variação', type: GridColTypeEnum.float_2, showTotal: true }))
            gridCredit.cols.push(new GridCol({ content: 'Percentual', type: GridColTypeEnum.percent }))
        }
        // grid lines
        for (const item of relat) {
            if (item.debitCredit !== EntryTypeEnum.credit) continue
            if (item.category.type === 'credito_a_desconsiderar') continue
            if (item.category.type === 'debito_a_desconsiderar') continue
            if (item.category.type === 'debito_distribuicao_lucro') continue

            const btnOpen = new GridButton({
                callback: () => goShowDetails(item.category.categoryId),
                icon: faEye,
            })
            const btnOpenPrevious = new GridButton({
                callback: () => goShowDetails(item.category.categoryId, 'previous'),
                icon: faEye,
            })
            let line: GridLine = new GridLine()
            line.slots.push({ content: item.category.label })
            if (showPrevious) {
                line.slots.push({ content: item.previousTotal })
                line.slots.push({ content: [btnOpenPrevious] })
            }
            line.slots.push({ content: item.total })
            line.slots.push({ content: [btnOpen] })
            if (showPrevious) {
                line.slots.push({ content: item.total - item.previousTotal })
                line.slots.push({ content: (100 / (item.previousTotal || 1)) * item.total - 100 })
            }
            gridCredit.lines.push(line)
        }
    }
    let gridDebit: GridClass = new GridClass()
    function loadGridDebit() {
        // grid
        gridDebit = new GridClass()
        // grid head
        gridDebit.title = 'Despesas'
        gridDebit.subtitle = ''
        gridDebit.fontSize = '15px'
        gridDebit.showActs = false
        gridDebit.cols.push(new GridCol({ content: 'Categoria', type: GridColTypeEnum.string }))
        if (showPrevious) {
            gridDebit.cols.push(new GridCol({ content: 'Período anterior', type: GridColTypeEnum.float_2, showTotal: true }))
            gridDebit.cols.push(new GridCol({ content: '', type: GridColTypeEnum.button }))
        }
        gridDebit.cols.push(new GridCol({ content: 'Valor', type: GridColTypeEnum.float_2, showTotal: true }))
        gridDebit.cols.push(new GridCol({ content: '', type: GridColTypeEnum.button }))
        if (showPrevious) {
            gridDebit.cols.push(new GridCol({ content: 'Variação', type: GridColTypeEnum.float_2, showTotal: true }))
            gridDebit.cols.push(new GridCol({ content: 'Percentual', type: GridColTypeEnum.percent }))
        }
        // grid lines
        for (const item of relat) {
            if (item.debitCredit !== EntryTypeEnum.debit) continue
            if (item.category.type === 'credito_a_desconsiderar') continue
            if (item.category.type === 'debito_a_desconsiderar') continue
            if (item.category.type === 'debito_distribuicao_lucro') continue

            const btnOpen = new GridButton({
                callback: () => goShowDetails(item.category.categoryId),
                icon: faEye,
            })
            const btnOpenPrevious = new GridButton({
                callback: () => goShowDetails(item.category.categoryId, 'previous'),
                icon: faEye,
            })
            let line: GridLine = new GridLine()
            line.slots.push({ content: item.category.label })
            if (showPrevious) {
                line.slots.push({ content: item.previousTotal })
                line.slots.push({ content: [btnOpenPrevious] })
            }
            line.slots.push({ content: item.total })
            line.slots.push({ content: [btnOpen] })
            if (showPrevious) {
                line.slots.push({ content: item.total - item.previousTotal })
                line.slots.push({ content: (100 / (item.previousTotal || 1)) * item.total - 100 })
            }
            gridDebit.lines.push(line)
        }
    }
    let gridCreditNoImpact: GridClass = new GridClass()
    function loadGridCreditNoImpact() {
        // grid
        gridCreditNoImpact = new GridClass()
        // grid head
        gridCreditNoImpact.title = 'Entradas sem Impacto no Resultado'
        gridCreditNoImpact.fontSize = '15px'
        gridCreditNoImpact.showActs = false
        gridCreditNoImpact.cols.push(new GridCol({ content: 'Categoria', type: GridColTypeEnum.string }))
        if (showPrevious) {
            gridCreditNoImpact.cols.push(new GridCol({ content: 'Período anterior', type: GridColTypeEnum.float_2, showTotal: true }))
            gridCreditNoImpact.cols.push(new GridCol({ content: '', type: GridColTypeEnum.button }))
        }
        gridCreditNoImpact.cols.push(new GridCol({ content: 'Valor', type: GridColTypeEnum.float_2, showTotal: true }))
        gridCreditNoImpact.cols.push(new GridCol({ content: '', type: GridColTypeEnum.button }))
        if (showPrevious) {
            gridCreditNoImpact.cols.push(new GridCol({ content: 'Variação', type: GridColTypeEnum.float_2, showTotal: true }))
            gridCreditNoImpact.cols.push(new GridCol({ content: 'Percentual', type: GridColTypeEnum.percent }))
        }
        // grid lines
        for (const item of relat) {
            if (item.debitCredit !== EntryTypeEnum.credit) continue
            if (
                item.category.type !== 'credito_a_desconsiderar' &&
                item.category.type !== 'debito_a_desconsiderar' &&
                item.category.type !== 'debito_distribuicao_lucro'
            ) {
                continue
            }

            const btnOpen = new GridButton({
                callback: () => goShowDetails(item.category.categoryId),
                icon: faEye,
            })
            const btnOpenPrevious = new GridButton({
                callback: () => goShowDetails(item.category.categoryId, 'previous'),
                icon: faEye,
            })
            let line: GridLine = new GridLine()
            line.slots.push({ content: item.category.label })
            if (showPrevious) {
                line.slots.push({ content: item.previousTotal })
                line.slots.push({ content: [btnOpenPrevious] })
            }
            line.slots.push({ content: item.total })
            line.slots.push({ content: [btnOpen] })
            if (showPrevious) {
                line.slots.push({ content: item.total - item.previousTotal })
                line.slots.push({ content: (100 / (item.previousTotal || 1)) * item.total - 100 })
            }
            gridCreditNoImpact.lines.push(line)
        }
    }
    let gridDebitNoImpact: GridClass = new GridClass()
    function loadGridDebitNoImpact() {
        // grid
        gridDebitNoImpact = new GridClass()
        // grid head
        gridDebitNoImpact.title = 'Saídas sem Impacto no Resultado'
        gridDebitNoImpact.fontSize = '15px'
        gridDebitNoImpact.showActs = false
        gridDebitNoImpact.cols.push(new GridCol({ content: 'Categoria', type: GridColTypeEnum.string }))
        if (showPrevious) {
            gridDebitNoImpact.cols.push(new GridCol({ content: 'Período anterior', type: GridColTypeEnum.float_2, showTotal: true }))
            gridDebitNoImpact.cols.push(new GridCol({ content: '', type: GridColTypeEnum.button }))
        }
        gridDebitNoImpact.cols.push(new GridCol({ content: 'Valor', type: GridColTypeEnum.float_2, showTotal: true }))
        gridDebitNoImpact.cols.push(new GridCol({ content: '', type: GridColTypeEnum.button }))
        if (showPrevious) {
            gridDebitNoImpact.cols.push(new GridCol({ content: 'Variação', type: GridColTypeEnum.float_2, showTotal: true }))
            gridDebitNoImpact.cols.push(new GridCol({ content: 'Percentual', type: GridColTypeEnum.percent }))
        }
        // grid lines
        for (const item of relat) {
            if (item.debitCredit !== EntryTypeEnum.debit) continue
            if (
                item.category.type !== 'credito_a_desconsiderar' &&
                item.category.type !== 'debito_a_desconsiderar' &&
                item.category.type !== 'debito_distribuicao_lucro'
            ) {
                continue
            }

            const btnOpen = new GridButton({
                callback: () => goShowDetails(item.category.categoryId),
                icon: faEye,
            })
            const btnOpenPrevious = new GridButton({
                callback: () => goShowDetails(item.category.categoryId, 'previous'),
                icon: faEye,
            })
            let line: GridLine = new GridLine()
            line.slots.push({ content: item.category.label })
            if (showPrevious) {
                line.slots.push({ content: item.previousTotal })
                line.slots.push({ content: [btnOpenPrevious] })
            }
            line.slots.push({ content: item.total })
            line.slots.push({ content: [btnOpen] })
            if (showPrevious) {
                line.slots.push({ content: item.total - item.previousTotal })
                line.slots.push({ content: (100 / (item.previousTotal || 1)) * item.total - 100 })
            }
            gridDebitNoImpact.lines.push(line)
        }
    }

    let categoryId: string = null
    let detailsEntries: Entry[] = []
    function goShowDetails(_categoryId: string, type: 'current' | 'previous' = 'current') {
        if (type === 'current') {
            detailsEntries = relat.find((el) => el.category.categoryId === _categoryId).entries
        } else {
            detailsEntries = relat.find((el) => el.category.categoryId === _categoryId).previousEntries
        }
        categoryId = _categoryId
    }

    function formChanged() {
        loadEntities()
    }

    let chartCredit: ChartData
    function loadChartCredit() {
        let items: { label: string; value: number }[] = []
        for (const el of relat) {
            if (el.debitCredit !== EntryTypeEnum.credit) continue
            if (el.category.type === 'credito_a_desconsiderar') continue
            if (el.category.type === 'debito_a_desconsiderar') continue
            if (el.category.type === 'debito_distribuicao_lucro') continue
            items.push({
                label: el.category.label,
                value: el.total,
            })
        }
        items.sort(window.api.utils.utils.dynamicSortMultiple(false, 'value')).reverse()

        chartCredit = new ChartData({
            categories: [...items.map((el) => el.label)],
            type: 'donut',
            title: 'Receitas',
            series: [...items.map((el) => el.value)],
        })
    }

    let chartDebit: ChartData
    function loadChartDebit() {
        let items: { label: string; value: number }[] = []
        for (const el of relat) {
            if (el.debitCredit !== EntryTypeEnum.debit) continue
            if (el.category.type === 'credito_a_desconsiderar') continue
            if (el.category.type === 'debito_a_desconsiderar') continue
            if (el.category.type === 'debito_distribuicao_lucro') continue
            items.push({
                label: el.category.label,
                value: el.total,
            })
        }
        items.sort(window.api.utils.utils.dynamicSortMultiple(false, 'value')).reverse()

        chartDebit = new ChartData({
            categories: [...items.map((el) => el.label)],
            type: 'donut',
            title: 'Despesas',
            series: [...items.map((el) => el.value)],
        })
    }

    let chartFlow: ChartData
    function loadChartFlow(entries: Entry[]) {
        const date = new Date(dates.from)
        let creditArray: number[] = []
        let debitArray: number[] = []
        let resultArray: number[] = []
        let labels: string[] = []
        let total = 0
        while (date.getTime() < dates.to.getTime()) {
            const day = new Date(date)
            date.setDate(day.getDate() + 1)
            const fromToday: Entry[] = []
            for (const entry of entries) {
                if (entry.category.type === 'credito_a_desconsiderar') continue
                if (entry.category.type === 'debito_a_desconsiderar') continue
                if (entry.category.type === 'debito_distribuicao_lucro') continue
                if (window.api.utils.dates.setDateStringToLocalMidnight(entry.paymentAt.toString()).getTime() === day.getTime()) {
                    fromToday.push(entry)
                }
            }
            let credit = fromToday.filter((el) => el.type === EntryTypeEnum.credit).reduce((acc, el) => acc + el.value, 0)
            let debit = fromToday.filter((el) => el.type === EntryTypeEnum.debit).reduce((acc, el) => acc - el.value, 0)
            credit = Math.round(credit * 100) / 100
            debit = Math.round(debit * 100) / 100
            //if (credit === 0 && debit === 0) continue
            total = Math.round((total + credit + debit) * 100) / 100
            resultArray.push(total)
            creditArray.push(credit)
            debitArray.push(debit)
            labels.push(dayjs(day).format('DD/MM'))
        }
        const dados = [
            { name: 'Crédito', data: creditArray, type: 'column' },
            { name: 'Débito', data: debitArray, type: 'column' },
            { name: 'Acumulado', data: resultArray, type: 'line' },
        ]
        chartFlow = new ChartData({
            categories: labels,
            type: 'line',
            stacked: true,
            title: 'Geração de Caixa',
            series: dados,
            colors: ['#008FFB', '#FF4560', '#5068e2'],
            showTotals: false,
            showGrid: true,
            height: 450,
            showToolbar: true,
        })
    }
</script>

{#if categoryId}
    <Popover title="Detalhamento" width="1250px" height="100px" on:close={() => (categoryId = null)}>
        <Financial_4RelatResultDetail entries={detailsEntries} on:change={formChanged}></Financial_4RelatResultDetail>
    </Popover>
{/if}

<Content footer={true}>
    <Zact>
        <DateSelectMonth bind:this={dateSelect} startFrom={dates.from} startTo={dates.to} on:change={dateChanged} />
        <Zinput type="toggle" bind:value={showPrevious} on:change={loadEntities} label="Comparar com período anterior"></Zinput>
        <div class="spacer h-mobile" />
        <button class="btn btn-secondary h-mobile" on:click={() => window.print()}><Fa icon={faPrint} /></button>
    </Zact>

    <div class="content-columns h-print">
        <div>
            <Zchart data={chartFlow} class="h-space"></Zchart>
        </div>
    </div>

    <div class="content-columns finan-charts h-print">
        <div>
            <Zchart data={chartCredit} class="h-space"></Zchart>
        </div>
        <div>
            <Zchart data={chartDebit} class="h-space"></Zchart>
        </div>
    </div>

    <Zflow>
        <Zframe title="Receitas e Despesas" class="h-space">
            <div class="text-muted">
                Categorize todas as suas movimentações e tenha controle completo de suas finanças.<br />
                Utilize estas informações para identificar as categorias de receita e despesa que geram maior impacto em suas contas.
            </div>
        </Zframe>
        <Zgrid grid={gridCredit} />
        <Zgrid grid={gridDebit} />
    </Zflow>

    <Zflow class="h-space">
        <Zframe title="Movimentações Sem Impacto no Resultado" class="h-space">
            <div class="text-muted">
                Algumas operações financeiras não geram impacto em seus resultados, por isso elas não são exibidas junto com Receitas e Despesas.<br />
                Alguns exemplos dessas operações:<br />
                <ul>
                    <li>Um PIX que foi enviado errado e estornou;</li>
                    <li>Um pagamento em duplicidade onde o valor de um deles foi estornado;</li>
                    <li>Distribuição de lucros, pois o lucro não é uma despesa da empresa;</li>
                    <li>Transferências entre contas da empresa;</li>
                    <li>Compra ou resgate de títulos de capitalização e investimentos.</li>
                </ul>
                Estas movimentaçãoes serão exibidas abaixo para facilitar correções e gerar uma melhor compreensão de sua movimentação.
            </div>
        </Zframe>
        <Zgrid grid={gridCreditNoImpact} />
        <Zgrid grid={gridDebitNoImpact} />
    </Zflow>

    <LoadSpinner {show} />
</Content>

<style>
    .content-columns {
        display: flex;
        gap: 8px;
    }
    .content-columns > * {
        flex: 1 1 100px;
        display: flex;
    }
    @media (max-width: 900px) {
    }
    @media (min-width: 1920px) {
    }

    @media print {
    }
</style>
